import { apiRequest } from '../../shared/api/client';

/**
 * Picks and pick faces — mirrors Invetrak.Api.Endpoints.PickEndpoints and
 * ItemPickLocationEndpoints.
 *
 *   GET    /api/v1/picks                              picks:read
 *   GET    /api/v1/picks/{id}                         picks:read
 *   GET    /api/v1/picks/{id}/lines                   picks:read
 *   GET    /api/v1/picks/{id}/directed                picks:read
 *   POST   /api/v1/picks/{id}/lines/{lineId}/pick     picks:post
 *   POST   /api/v1/picks/{id}/lines/{lineId}/reverse  picks:post
 *   POST   /api/v1/picks/{id}/license-plate           picks:post
 *   POST   /api/v1/picks/{id}/close-short             picks:post
 *   POST   /api/v1/picks/{id}/ship                    orders:write
 *   GET    /api/v1/picks/{id}/packing-list            picks:read
 *   POST   /api/v1/picks/{id}/packing-list            picks:post
 *   GET    /api/v1/pick-faces                 locations:read
 *   PUT    /api/v1/pick-faces                 locations:write
 *   DELETE /api/v1/pick-faces/{id}            locations:write
 *
 * **Nothing creates a pick:** releasing a sales order does that, and it stays the only way — a
 * second route to make one exist is a second way for the same stock to be promised twice.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

/** 001's four. A pick opens, is walked, and ends completed or cancelled. */
export type PickStatus = 'open' | 'in_progress' | 'completed' | 'cancelled';

/** Work still on the floor. What the list opens on, as legacy's `IsShipped = 0` does. */
export const OPEN_PICK_STATUSES: PickStatus[] = ['open', 'in_progress'];

/** F walks the aisle one way, R the other. legacy's @sortOrder. */
export type WalkDirection = 'F' | 'R';

export interface PickDto {
  pickId: string;
  pickNumber: string;
  /** directed, sales_order, license_plate, wave or work_order. */
  pickType: string;
  status: PickStatus;
  warehouseId: string;
  warehouseCode: string;
  /** Null once M5.7's wave pick lands: a wave spans several orders. */
  salesOrderId: string | null;
  orderNumber: string | null;
  customerPo: string | null;
  referenceNumber: string | null;
  /** The ORDER's status, not the pick's. An order can be closed under a live pick. */
  orderStatus: string | null;
  requestedShipDateUtc: string | null;
  customerId: string | null;
  customerNumber: string | null;
  customerName: string | null;
  shipToName: string | null;
  pickerUserId: string | null;
  pickerName: string | null;
  createdAtUtc: string;
  completedAtUtc: string | null;
  /**
   * Lines, not quantities, and there is no total on purpose: each line is in its own unit, so a
   * sum across them would add cases to eaches.
   */
  lineCount: number;
  linesOutstanding: number;
  /** The dock, captured from the FIRST line picked. Null until something is picked. */
  pickStagingLocationId: string | null;
  pickStagingLocationCode: string | null;
  /**
   * The stock was not there and nobody is going to find it.
   *
   * Not derivable from the status: a pick closed short and one that simply finished both read
   * `completed`, and the difference is whether somebody should go looking for the rest.
   */
  isClosedShort: boolean;
  /**
   * The shipment.
   *
   * There is no shipment record of its own: legacy keeps the bill of lading, the tracking number,
   * the carrier and the ship date on the pick header and has no shipment table at all. The pick
   * document IS the shipment.
   */
  isShipped: boolean;
  shippedAtUtc: string | null;
  shippedByUserId: string | null;
  shipMethodId: string | null;
  shipMethodCode: string | null;
  shipMethodName: string | null;
  trackingNumber: string | null;
  billOfLading: string | null;
  /** Whether anything is at the dock to send. A pick closed short having found none is not. */
  hasStagedStock: boolean;
}

export interface PickLineDto {
  pickLineId: string;
  pickId: string;
  lineNumber: number;
  salesOrderLineId: string | null;
  salesOrderLineNumber: number | null;
  itemId: string;
  itemNumber: string;
  itemDescription: string;
  isLotControlled: boolean;
  isSerialized: boolean;
  uomId: string;
  uomCode: string;
  quantityRequested: number;
  quantityPicked: number;
  quantityOutstanding: number;
  /** Where it actually came from. Null until somebody picks the line — M5.5. */
  fromLocationId: string | null;
  fromLocationCode: string | null;
  lotId: string | null;
  lotNumber: string | null;
  status: string;
}

/**
 * One stop on the route.
 *
 * An item with several faces appears once per face, each row carrying the WHOLE outstanding
 * quantity rather than a share of it — the picker takes what is at the first face and by the time
 * they reach the second the figure has fallen. `suggestedLocationId` is null when the item has no
 * face at all, which is a row that says "go and find this" rather than one that is missing.
 */
export interface DirectedPickLineDto {
  pickLineId: string;
  lineNumber: number;
  itemId: string;
  itemNumber: string;
  itemDescription: string;
  isLotControlled: boolean;
  isSerialized: boolean;
  uomId: string;
  uomCode: string;
  quantityRequested: number;
  quantityPicked: number;
  quantityOutstanding: number;
  suggestedLocationId: string | null;
  suggestedLocationCode: string | null;
  locationSequence: number | null;
  status: string;
}

/** Where an item is picked from. An item may have several faces in one warehouse. */
export interface PickFaceDto {
  itemPickLocationId: string;
  itemId: string;
  itemNumber: string;
  itemDescription: string;
  warehouseId: string;
  locationId: string;
  locationCode: string;
  locationName: string | null;
  /** The LOCATION's walk order. A face with none sorts last on every route. */
  sequence: number | null;
  isForwardPick: boolean;
  locationIsActive: boolean;
  createdAtUtc: string;
  lastModifiedUtc: string;
}

/** One line of the availability check — what the order can and cannot fill. */
export interface AvailabilityLineDto {
  salesOrderLineId: string;
  lineNumber: number;
  itemId: string;
  itemNumber: string;
  itemDescription: string;
  uomId: string;
  /** In the LINE's unit, for the screen. */
  quantityOutstanding: number;
  /** In STOCKING units, which is the only basis on which the next two can be compared. */
  outstandingStocking: number;
  availableToReserve: number;
  /** Available minus outstanding. Negative is short. */
  variance: number;
}

export async function listPicks(
  options: {
    openOnly?: boolean;
    status?: PickStatus;
    salesOrderId?: string;
    /** Finished, unshipped, and with something at the dock. Wins over openOnly on the server. */
    toShip?: boolean;
  } = {},
  signal?: AbortSignal,
): Promise<PickDto[]> {
  const params = new URLSearchParams();
  // Sent explicitly rather than left to the server's default, so the All tab means all.
  params.set('openOnly', options.openOnly === false ? 'false' : 'true');
  if (options.toShip) params.set('toShip', 'true');
  if (options.status) params.set('status', options.status);
  if (options.salesOrderId) params.set('salesOrderId', options.salesOrderId);

  const { data } = await apiRequest<PickDto[]>(`/v1/picks/?${params.toString()}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function getPick(id: string, signal?: AbortSignal): Promise<PickDto> {
  const { data } = await apiRequest<PickDto>(`/v1/picks/${encodeURIComponent(id)}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/** The document: every line, including finished ones, in line order. */
export async function listPickLines(id: string, signal?: AbortSignal): Promise<PickLineDto[]> {
  const { data } = await apiRequest<PickLineDto[]>(`/v1/picks/${encodeURIComponent(id)}/lines`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/** The route: only what is still owed, one stop per pick face, in walk order. */
export async function getDirectedPick(
  id: string,
  sortOrder: WalkDirection = 'F',
  signal?: AbortSignal,
): Promise<DirectedPickLineDto[]> {
  const { data } = await apiRequest<DirectedPickLineDto[]>(
    `/v1/picks/${encodeURIComponent(id)}/directed?sortOrder=${sortOrder}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function listPickFaces(
  options: { itemId?: string } = {},
  signal?: AbortSignal,
): Promise<PickFaceDto[]> {
  const query = options.itemId ? `?itemId=${encodeURIComponent(options.itemId)}` : '';
  const { data } = await apiRequest<PickFaceDto[]>(`/v1/pick-faces/${query}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/**
 * Adds a face, or touches one the item already has.
 *
 * The warehouse is not a parameter: the server takes it from the session, because a face in another
 * warehouse is a refusal rather than an option.
 */
export async function savePickFace(itemId: string, locationId: string): Promise<PickFaceDto> {
  const { data } = await apiRequest<PickFaceDto>('/v1/pick-faces/', {
    method: 'PUT',
    body: { itemId, locationId },
    ...COOKIE_INIT,
  });
  return data;
}

export async function deletePickFace(id: string): Promise<void> {
  await apiRequest<void>(`/v1/pick-faces/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...COOKIE_INIT,
  });
}

/** What the order can and cannot fill, before anybody presses Release. */
export async function checkAvailability(
  salesOrderId: string,
  signal?: AbortSignal,
): Promise<AvailabilityLineDto[]> {
  const { data } = await apiRequest<AvailabilityLineDto[]>(
    `/v1/sales-orders/${encodeURIComponent(salesOrderId)}/availability`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Records a pick: the stock moves from the face to the dock under PK1.
 *
 * `quantity` is in the LINE's unit — the picker was told to fetch three cases, so three is what
 * they report. Returns the whole line list, because picking the last line completes the document.
 */
export async function pickLine(
  pickId: string,
  pickLineId: string,
  body: {
    quantity: number;
    fromLocationId: string;
    toLocationId: string;
    lotNumber?: string | null;
    serialNumber?: string | null;
  },
): Promise<PickLineDto[]> {
  const { data } = await apiRequest<PickLineDto[]>(
    `/v1/picks/${encodeURIComponent(pickId)}/lines/${encodeURIComponent(pickLineId)}/pick`,
    { method: 'POST', body, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Picks a whole license plate: one scan for what would otherwise be a line at a time.
 *
 * Takes only what the order still owes — a pallet of 100 against a line owing 60 sends 60 and
 * leaves 40 on the plate — and credits the line in the ORDER's unit, converting from the stocking
 * units the plate is counted in. legacy does neither.
 */
export async function pickLicensePlate(
  pickId: string,
  body: { licensePlateId: string; toLocationId: string },
): Promise<PickLineDto[]> {
  const { data } = await apiRequest<PickLineDto[]>(
    `/v1/picks/${encodeURIComponent(pickId)}/license-plate`,
    { method: 'POST', body, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Puts a picked quantity back, under PK2.
 *
 * Omitting `toLocationId` means "back where it came from", which the server reads off the line.
 */
/**
 * Puts a picked quantity back.
 *
 * `lotNumber` is only needed when the line was picked from more than one lot: the server falls
 * back to the line's own lot and refuses (409 lot_ambiguous) only when there is nothing to fall
 * back to. Guessing would put the wrong lot on the shelf and leave the right one short.
 *
 * `serialNumber` is the same contract with a sharper edge (086): for a serialized item ANY line
 * of more than one unit is "mixed", because each scan is a different unit. So a one-unit line
 * needs nothing and anything larger must name the unit going back, or 409 serial_ambiguous.
 */
export async function reversePickLine(
  pickId: string,
  pickLineId: string,
  body: {
    quantity: number;
    toLocationId?: string | null;
    lotNumber?: string | null;
    serialNumber?: string | null;
  },
): Promise<PickLineDto[]> {
  const { data } = await apiRequest<PickLineDto[]>(
    `/v1/picks/${encodeURIComponent(pickId)}/lines/${encodeURIComponent(pickLineId)}/reverse`,
    { method: 'POST', body, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Stops pretending the pick is live.
 *
 * With something picked the document completes, flagged short, and the order reads `picked`. With
 * nothing picked it is cancelled and the order goes back to `open` — which is the only way a
 * released order becomes releasable again.
 */
export async function closePickShort(pickId: string): Promise<PickDto> {
  const { data } = await apiRequest<PickDto>(
    `/v1/picks/${encodeURIComponent(pickId)}/close-short`,
    { method: 'POST', body: {}, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Ship confirm: the staged stock leaves the building.
 *
 * This is what writes QuantityShipped on the order, and so what turns an order picked short into a
 * back order whose remainder can be released again. Every field is optional, as legacy's is.
 */
export async function shipPick(
  pickId: string,
  body: {
    shipMethodId?: string | null;
    trackingNumber?: string | null;
    billOfLading?: string | null;
  } = {},
): Promise<PickDto> {
  const { data } = await apiRequest<PickDto>(`/v1/picks/${encodeURIComponent(pickId)}/ship`, {
    method: 'POST',
    body,
    ...COOKIE_INIT,
  });
  return data;
}

/** Nothing further will happen to this pick by itself. */
export function isFinishedPick(status: PickStatus): boolean {
  return status === 'completed' || status === 'cancelled';
}

/** Picked, not yet gone, and with something at the dock to send. */
export function isReadyToShip(pick: PickDto): boolean {
  return pick.status === 'completed' && !pick.isShipped && pick.hasStagedStock;
}

// ---------------------------------------------------------------------------
// The packing list — M5.8, tenant script 072
// ---------------------------------------------------------------------------

/**
 * One line of a packing list, as the API returns it.
 *
 * **Almost all of this is frozen.** The item, the quantities and the addresses were copied into
 * `tbl_PackingList` at ship confirm and are the document as it was issued — correcting the
 * customer's address next week does not change them. Only `cartonCount`, `trackingNumber`,
 * `billOfLading` and `shippedAtUtc` are read live off the pick.
 *
 * **The shipment fields repeat on every line.** That is what legacy's report data source is, and
 * it is why this screen composes its header from row one rather than expecting two shapes.
 */
export interface PackingListLineDto {
  packingListLineId: string;
  pickId: string;
  /** The shipment number. The pick IS the shipment — there is no shipment table. */
  pickNumber: string;
  warehouseId: string;
  salesOrderId: string | null;
  orderNumber: string | null;
  salesOrderLineId: string | null;

  /** Live off the pick: the packer may still be counting. */
  cartonCount: number | null;
  shippedAtUtc: string | null;
  trackingNumber: string | null;
  billOfLading: string | null;

  itemId: string;
  itemNumber: string;
  itemDescription: string | null;
  uomCode: string | null;
  lotNumber: string | null;
  serialNumber: string | null;
  /** In the ORDER LINE's unit, not the stocking unit. */
  quantityOrdered: number;
  /** What went in this box. */
  quantityShipped: number;
  /** What was owed BEFORE this shipment — legacy's meaning of the name. */
  balanceDue: number;
  /** What is still owed after it: `balanceDue - quantityShipped`. */
  quantityBackOrdered: number;
  customerPo: string | null;
  shipMethodName: string | null;
  shipFromName: string | null;
  shipFromAddressLine1: string | null;
  shipFromAddressLine2: string | null;
  shipFromCity: string | null;
  shipFromState: string | null;
  shipFromPostalCode: string | null;
  shipToName: string | null;
  shipToAddressLine1: string | null;
  shipToAddressLine2: string | null;
  shipToCity: string | null;
  shipToState: string | null;
  shipToPostalCode: string | null;
  createdAtUtc: string;
}

/**
 * The document as issued.
 *
 * An empty array is not an error: it means one has never been generated, either because the pick
 * has not shipped or because it shipped before tenant script 072 was applied.
 */
export async function fetchPackingList(
  pickId: string,
  signal?: AbortSignal,
): Promise<PackingListLineDto[]> {
  const { data } = await apiRequest<PackingListLineDto[]>(
    `/v1/picks/${encodeURIComponent(pickId)}/packing-list`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * legacy's Pack List button: make the document if it is not there, stamp the carton count, hand it
 * back.
 *
 * **Generating twice does not reissue it.** The first document is the document — the copy that went
 * in the box — so a second call re-stamps the carton count and returns what already exists.
 *
 * Omitting `cartonCount` leaves the stored one alone rather than blanking it, which is what makes
 * re-printing safe.
 */
export async function generatePackingList(
  pickId: string,
  body: { cartonCount?: number | null } = {},
): Promise<PackingListLineDto[]> {
  const { data } = await apiRequest<PackingListLineDto[]>(
    `/v1/picks/${encodeURIComponent(pickId)}/packing-list`,
    { method: 'POST', body, ...COOKIE_INIT },
  );
  return data;
}
